import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { FormEvent, memo, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useLoaderData, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import ObjectSuspense from '../../../components/common/ObjectSuspense';
import { EditorContext, SuperEditorContext } from '../../../components/common/PermissionContext';
import setTitle from '../../../components/common/SetTitle';
import { joinWithoutDuplicates } from '../../../components/common/utilities/Utils';
import EfasLink from '../../../components/EfasLink';
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import SetReturnStateLink from '../../../components/SetReturnStateLink';
import { OutletIProps } from '../../../components/sitebase/Base.types';
import { NotificationNavLinks } from '../notifications/NotificationLinks';
import type { Feedback } from './Feedback.model';
import { makePartnerSentRoute } from './Urls';

import 'react-toastify/dist/ReactToastify.css';
import "./Feedback.css";
import type { EFASNotification } from '../notifications/Notification.types';

function FlashFeedbackInfo(props: { notification: EFASNotification, feedback?: Feedback, disabled?: boolean }) {

    const regions = [<option key="All affected regions" value={"All affected regions"}>All affected regions</option>].concat(props.notification.alert_points_expanded.map(ap => {
        const value = `${ap.ad2_name} - ${ap.basin_name} - ${ap.river_name}`;
        return <option key={ap.ad2_name} value={value}>{value}</option>
    }));

    return <div className='d-flex flex-column' data-testid="notification-info">
        <h2>
            Flash-flood notification info
        </h2>

        <span><b>Date:</b> {props.notification.alert_points_expanded[0].forecast_date}</span>
        <span><b>Country:</b> {props.notification.alert_points_expanded.map(f => f.country).join(',')}</span>
        <span><b>Basin:</b> {props.notification.alert_points_expanded.map(f => f.basin_name).join(', ')}</span>
        <span><b>River:</b> {props.notification.alert_points_expanded.map(f => f.river_name).join(', ')}</span>

        <div className='d-flex flex-column'><div className='my-auto'><label htmlFor='feedback_region'><b>Region (Region - Basin - River):</b></label></div>
            <select className='form-select' name='feedback_region' defaultValue={props.feedback ? props.feedback.feedback_region : ""} disabled={props.disabled}>
                {regions}
            </select>
        </div>
    </div>
};

function FloodFeedbackInfo(props: { notification: EFASNotification }) {
    return <div className='d-flex flex-column' data-testid="notification-info">
        <h3>
            Flood notification - <i>{props.notification.notification_type === 1 ? "Formal" : "Informal"}</i>
        </h3>

        <span><b>Date:</b> {props.notification.alert_points_expanded[0].forecast_date}</span>
        <span><b>Country:</b> {joinWithoutDuplicates(props.notification.alert_points_expanded.map(f => f.country))}</span>
        <span><b>Basin:</b> {joinWithoutDuplicates(props.notification.alert_points_expanded.map(f => f.basin_name))}</span>
        <span><b>River:</b> {joinWithoutDuplicates(props.notification.alert_points_expanded.map(f => f.river_name))}</span>
    </div>
};

export const ButtonGroup = memo(function (props: { name: string, className?: string, checkedValue: string | null, values: { name: string, value: string, color: string }[], disabled?: boolean, required?: boolean, onChange?: (value: string) => void }) {
    return <div className={`align-middle m-auto ${props.className}`}>
        <div className="btn-group d-flex flex-row flex-lg-nowrap flex-wrap" role="group" aria-label="">
            {
                props.values.map((item) => {
                    return <>
                        <input disabled={props.disabled}
                            type="radio"
                            className="btn-check"
                            name={props.name}
                            id={`${props.name}-${item.value}`}
                            value={item.value}
                            autoComplete="off"
                            required={props.required}
                            defaultChecked={props.checkedValue === item.value}
                            onClick={() => props.onChange ? props.onChange(item.value) : null} />
                        <label className={`btn btn-outline-yellow feedback-group-button ${item.color}`} htmlFor={`${props.name}-${item.value}`}>{item.name}</label>
                    </>
                })
            }
        </div>
    </div>;
});

export interface ButtonGroupRankingValues {
    name: string,
    value: number,
    order: number,
}
export const ButtonGroupRanking = memo(function (props: {
    name: string,
    className?: string,
    checkedValue?: number[] | undefined,
    values: ButtonGroupRankingValues[],
    disabled?: boolean,
    selectedCausesRef: RefObject<HTMLInputElement | null>,
}) {
    const [selected, setSelected] = useState<ButtonGroupRankingValues[]>(props.checkedValue ? props.values.filter(v => props.checkedValue?.includes(v.value)).sort((a, b) => a.order - b.order) : []);
    const [options, setOptions] = useState<ButtonGroupRankingValues[]>(props.values.filter(v => !selected.includes(v)).sort((a, b) => a.order - b.order));

    const handleAdd = useCallback((value: ButtonGroupRankingValues) => {
        setSelected([...selected, value]);
        setOptions(options.filter(v => v !== value).sort((a, b) => a.order - b.order));
    }, [selected, options]);

    const handleRemove = useCallback((value: ButtonGroupRankingValues) => {
        setSelected(selected.filter(v => v !== value));
        setOptions([...options, value].sort((a, b) => a.order - b.order));
    }, [selected, options]);

    return <div className={`align-middle m-auto ${props.className}`}>
        <div data-testid="available-causes" className='d-flex flex-row flex-lg-nowrap flex-wrap'>
            <div className='text-center'>
                <h6>Available causes</h6>
                <div className="btn-group-vertical d-flex px-3 feedback-causes" role="group">
                    {
                        options.map(item => {
                            return <>
                                <input type="radio" disabled={props.disabled} className="btn-check feedback-btn-check" id={`${props.name}-${item.value}`} autoComplete="off"
                                    onClick={(ev) => { ev.preventDefault(); handleAdd(item) }} />
                                <label className="btn btn-outline-dark" htmlFor={`${props.name}-${item.value}`} >{item.name}</label>
                            </>
                        })
                    }
                </div>
            </div>
            <div className='text-center'>
                <h6>Selected</h6>
                <div data-testid="selected-causes" className="btn-group-vertical d-flex px-3 feedback-causes" role="group">
                    {
                        selected.length === 0 ?
                            <>
                                <input type='radio' className="btn-check" required id='prevent-submission-without-causes' ref={props.selectedCausesRef} />
                                <label className="btn btn-outline-danger" htmlFor="prevent-submission-without-causes">No causes selected! Please select one from the left</label>
                            </>
                            :
                            selected.map(item => {
                                return <>
                                    <input type="radio" disabled={props.disabled} className="btn-check" name={`${props.name}-${item.value}`} id={`${props.name}-${item.value}`} autoComplete="off" defaultChecked={true}
                                        onClick={(ev) => { ev.preventDefault(); handleRemove(item) }} />
                                    <label className="feedback-observed-button-active feedback-observed-button btn btn-outline-dark" htmlFor={`${props.name}-${item.value}`} >{item.name}</label>
                                </>
                            })
                    }
                </div>
            </div>
        </div>

    </div>;
});

const FlashFloodForm = memo(function (props: { feedback?: Feedback, disabled: boolean }) {
    return <div className='d-flex py-4'>
        <label className='feedback-about-row-heading flex-fill' htmlFor='comment_notification'>Additional information</label>
        <textarea data-testid="comment_notification" className='form-control feedback-about-row-body' name='comment_notification' defaultValue={props.feedback?.comment_notification || ""} disabled={props.disabled} style={{ height: "30vh" }}
        ></textarea>
    </div>
});

const AddedValue = memo(function (props: { feedback?: Feedback, disabled: boolean }) {
    return <div className='py-3'>
        <h1>Extra Notes</h1>
        <div data-testid="added_value" className='d-flex flex-row flex-lg-nowrap flex-wrap py-2'>
            <label className='feedback-about-row-heading flex-fill' htmlFor='added_value'><h6>How would you rate the added value of the forecast? *</h6></label>
            <ButtonGroup name='added_value' className='feedback-about-row-body' disabled={props.disabled} required={true} checkedValue={`${props.feedback?.added_value}`} values={[
                { name: "Not known", value: "0", color: "color-7" },
                { name: "1", value: "1", color: "color-8" },
                { name: "2", value: "2", color: "color-9" },
                { name: "3", value: "3", color: "color-10" },
                { name: "4", value: "4", color: "color-11" },
                { name: "5", value: "5", color: "color-12" }
            ]} />
        </div>
        <div className='d-flex flex-row flex-lg-nowrap flex-wrap py-2'>
            <label className='feedback-about-row-heading flex-fill' htmlFor="news_links">Web Links providing additional information (<i>One per line</i>)</label>
            <textarea data-testid="news_links" className="form-control feedback-about-row-body" name="news_links"
                defaultValue={props.feedback?.news_links || ""}
                rows={props.feedback?.news_links?.split("\n").length || 1}
                disabled={props.disabled} />
        </div>
        <div className='d-flex flex-row flex-lg-nowrap flex-wrap py-2'>
            <label className='feedback-about-row-heading flex-fill' htmlFor="further_comments">Other Comments?</label>
            <textarea data-testid="further_comments" className="form-control feedback-about-row-body" name="further_comments"
                defaultValue={props.feedback?.further_comments || ""} disabled={props.disabled} />
        </div>
    </div>
});

const NotObservedForm = memo(function (props: { feedback: Feedback, disabled: boolean }) {
    return <>
        <div className='d-flex py-4'>
            <label className='feedback-about-row-heading flex-fill' htmlFor='no_floods_comments'>Please enter any known reason relevant to why the event did not occur as forecast<br />E.g. reservoir, snowfall, precipitation outside catchment or did not occur, no snowmelt etc... *</label>
            <textarea data-testid='no_floods_comments' className='form-control feedback-about-row-body' name='no_floods_comments' defaultValue={props.feedback?.no_floods_comments || ""} disabled={props.disabled}></textarea>
        </div>
        <AddedValue feedback={props.feedback} disabled={props.disabled} />
    </>
});

const ObservedForm = memo(function (props: { feedback?: Feedback, disabled: boolean, selectedCausesRef: React.MutableRefObject<HTMLInputElement | undefined> }) {
    return <>
        <div className="py-4">
            <h1>About the Event</h1>
            <h3>How severe was the event?</h3>
            <div data-testid="return_period" className="d-flex flex-row flex-lg-nowrap flex-wrap py-2">
                <label htmlFor='return_period' className='feedback-about-row-heading flex-fill'>Return Period *</label>
                <ButtonGroup name='return_period' className='feedback-about-row-body' disabled={props.disabled} required={true} checkedValue={`${props.feedback?.return_period.id}`} values={[
                    { name: "2-4 years", value: "1", color: "color-12" },
                    { name: "5-9 years", value: "2", color: "color-11" },
                    { name: "10-19 years", value: "3", color: "color-10" },
                    { name: "20-50 years", value: "4", color: "color-9" },
                    { name: ">= 100 years", value: "5", color: "color-8" },
                    { name: "Not known", value: "6", color: "color-7" },
                ]} />
            </div>
        </div>
        <div className="d-flex flex-row flex-lg-nowrap flex-wrap py-1">
            <label htmlFor='severity' className='feedback-about-row-heading flex-fill'>What was the severity of the event (return period, area, water level above land, casualties, economic damage)? *</label>
            <textarea data-testid="severity" name='severity' className='form-control feedback-about-row-body' defaultValue={props.feedback?.severity || ""} disabled={props.disabled} required />
        </div>
        <div className="d-flex flex-row flex-lg-nowrap flex-wrap py-1">
            <label htmlFor='causes' className='feedback-about-row-heading flex-fill'>What caused the event?</label>
            <ButtonGroupRanking name='causes' className='feedback-about-row-body' disabled={props.disabled} selectedCausesRef={props.selectedCausesRef}
                checkedValue={props.feedback?.flood_cause.sort(fc => fc.ranking).map(fc => fc.id)}
                values={[
                    { name: "Dam break", value: 1, order: 1 },
                    { name: "Extreme rainfall", value: 2, order: 2 },
                    { name: "Snow melt", value: 3, order: 3 },
                    { name: "Ice jam", value: 6, order: 4 },
                    { name: "Long lasting rainfall", value: 4, order: 5 },
                    { name: "Soil saturation", value: 5, order: 6 },
                    { name: "None of the listed", value: 7, order: 7 },
                ]} />
        </div>
        <div data-testid="leadtime_accuracy" className='py-3'>
            <h3>How much lead time gave the notification?</h3>
            <div className="d-flex flex-row flex-lg-nowrap flex-wrap py-1">
                <label htmlFor='leadtime_accuracy' className='feedback-about-row-heading flex-fill'>Number of days between issue of notification and start of the event *</label>
                <ButtonGroup name='leadtime_accuracy' className='feedback-about-row-body' disabled={props.disabled} required={true} checkedValue={props.feedback?.leadtime_accuracy || ""} values={[
                    { name: "0", value: "0", color: "color-12" },
                    { name: "1", value: "1", color: "color-11" },
                    { name: "2", value: "2", color: "color-10" },
                    { name: "3", value: "3", color: "color-9" },
                    { name: "4", value: "4", color: "color-8" },
                    { name: "5", value: "5", color: "color-7" },
                    { name: "6", value: "6", color: "color-6" },
                    { name: "7", value: "7", color: "color-5" },
                    { name: "8", value: "8", color: "color-4" },
                    { name: "9", value: "9", color: "color-3" },
                    { name: "10", value: "10", color: "color-2" },
                    { name: ">10", value: ">10", color: "color-1" }
                ]} />
            </div>
        </div>
        <div className='py-3'>
            <h1>About the Notification</h1>
            <h3>How accurate was the notification?</h3>
            <div data-testid="location_accuracy" className="d-flex flex-row flex-lg-nowrap flex-wrap py-1">
                <label htmlFor='location_accuracy' className='feedback-about-row-heading flex-fill'>Location *</label>
                <ButtonGroup name='location_accuracy' className='feedback-about-row-body' disabled={props.disabled} required={true} checkedValue={`${props.feedback?.location_accuracy.id}`} values={[
                    { name: "Not known", value: "1", color: "color-7" },
                    { name: "Not in the wider region", value: "6", color: "color-8" },
                    { name: "In the wider region", value: "5", color: "color-9" },
                    { name: "In the region but not in an adjacent catchment", value: "4", color: "color-10" },
                    { name: "Adjacent catchment", value: "3", color: "color-11" },
                    { name: "As indicated in EFAS information", value: "2", color: "color-12" }
                ]} />
            </div>

            <div data-testid="time_accuracy" className="d-flex flex-row flex-lg-nowrap flex-wrap py-1">
                <label htmlFor='time_accuracy' className='feedback-about-row-heading flex-fill'>Timing of onset *</label>
                <ButtonGroup name='time_accuracy' className='feedback-about-row-body' disabled={props.disabled} required={true} checkedValue={`${props.feedback?.time_accuracy.id}`} values={[
                    { name: "Not known", value: "1", color: "color-7" },
                    { name: "Start >= 3 days later than predicted", value: "4", color: "color-5" },
                    { name: "Start 1-2 days later than predicted", value: "3", color: "color-7" },
                    { name: "Start predicted on day", value: "2", color: "color-12" },
                    { name: "Start 1-2 days earlier than predicted", value: "7", color: "color-7" },
                    { name: "Start >= 3 days earlier than predicted", value: "5", color: "color-5" }
                ]} />
            </div>
            <div data-testid="peaktime_accuracy" className="d-flex flex-row flex-lg-nowrap flex-wrap py-1">
                <label htmlFor="peaktime_accuracy" className='feedback-about-row-heading flex-fill'>Timing of peak *</label>

                <ButtonGroup name='peaktime_accuracy' className='feedback-about-row-body' disabled={props.disabled} required={true} checkedValue={`${props.feedback?.peaktime_accuracy.id}`} values={[
                    { name: "Not known", value: "1", color: "color-7" },
                    { name: "Peak >= 3 days later than predicted", value: "3", color: "color-5" },
                    { name: "Peak 1-2 days later than predicted", value: "6", color: "color-7" },
                    { name: "Peak predicted on day", value: "5", color: "color-12" },
                    { name: "Peak 1-2 days earlier than predicted", value: "7", color: "color-7" },
                    { name: "Peak >= 3 days earlier than predicted", value: "4", color: "color-5" }
                ]} />
            </div>
            <div data-testid="magnitude_accuracy" className="d-flex flex-row flex-lg-nowrap flex-wrap py-1">
                <label className='feedback-about-row-heading flex-fill' htmlFor='magnitude_accuracy'>Peak magnitude *</label>
                <ButtonGroup name='magnitude_accuracy' className='feedback-about-row-body' disabled={props.disabled} required={true} checkedValue={`${props.feedback?.magnitude_accuracy.id}`} values={[
                    { name: "Not known", value: "2", color: "color-7" },
                    { name: "Peak much more severe than predicted", value: "4", color: "color-5" },
                    { name: "Peak more severe than predicted", value: "6", color: "color-7" },
                    { name: "Peak comparable to predicted", value: "1", color: "color-12" },
                    { name: "Peak less severe than predicted", value: "7", color: "color-7" },
                    { name: "Peak much less severe than predicted", value: "5", color: "color-5" }
                ]} />

            </div>
        </div>
        <AddedValue feedback={props.feedback} disabled={props.disabled} />
    </>;
});

export const FeedbackForm = memo(function () {
    const { notification_id, partner_abbr } = useParams();
    const feedbacks = (useLoaderData() as Feedback[]);
    const [feedback, setFeedback] = useState<Feedback>(feedbacks[0]);
    const [notification, setNotification] = useState<EFASNotification>();
    const selectedCauses = useRef<HTMLInputElement>(null);
    const [searchParams, _] = useSearchParams();

    const context = useOutletContext<OutletIProps>();

    const { setExtraMenuLinks } = context;

    const [feedbackLocked, setFeedbackLocked] = useState<boolean>(true);
    const [canBeEnabled, setCanBeEnabled] = useState<boolean>(partner_abbr === context.user.partner_abbr);
    const [disabled, setDisabled] = useState<boolean>(feedback?.partner_abbr !== undefined || searchParams.get("flood_obs") === null);
    const [floodObs, setFloodObs] = useState<string | null>(feedback?.flood_obs || searchParams.get("flood_obs") || null);
    const [observed, setObserved] = useState<boolean>(feedback?.flood_obs === "Yes");
    const [endDate, setEndDate] = useState<string | null>(null);
    const [daysLeft, setDaysLeft] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const onSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const endpoint = notification?.notification_type === 3 ? '/api/ff_feedbacks/flash-flood-feedbacks/' : '/api/feedbacks/feedbacks/';
        const method = feedback ? 'PATCH' : 'POST';
        const action = feedback ? `${endpoint}${feedback.id}/` : endpoint;

        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());

        data["user"] = context.user.username;
        data["alert_points"] = notification?.alert_points_expanded.map(ap => ap.id);
        data["notification"] = notification_id;
        data["notification_type"] = notification?.notification_type;

        // if these are not available set them to their "Not known" value equivalent
        if (data["location_accuracy"] === undefined) {
            data["location_accuracy"] = "1";
        }
        if (data["magnitude_accuracy"] === undefined) {
            data["magnitude_accuracy"] = "2";
        }
        if (data["peaktime_accuracy"] === undefined) {
            data["peaktime_accuracy"] = "1";
        }
        if (data["return_period"] === undefined) {
            data["return_period"] = "6";
        }
        if (data["time_accuracy"] === undefined) {
            data["time_accuracy"] = "1";
        }

        // convert the suffix of flood_cause-<value> to a list of {index:value}
        data["causes"] = {};
        const causes = Object.entries(data).filter(([key, _]) => key.startsWith("causes-"));

        if (selectedCauses.current && causes.length === 0) {
            selectedCauses.current?.setCustomValidity("Please select at least one cause");
            selectedCauses.current?.reportValidity();
            return;
        }

        causes.map(([key, _], index) => {
            return data["causes"][index] = parseInt(key.split("-")[1]);
        });

        fetch(action, {
            method: method,
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        }).then(response => {
            if (response.ok) {
                return response.json();
            } else if (response.status >= 400 && response.status < 599) {
                throw response.json();
            }
        }).then((feedback: Feedback) => {
            // show toast
            if (feedback) {
                toast("Feedback submitted successfully", { position: 'top-center', type: 'success' });
                setDisabled(true);
                setCanBeEnabled(true);
                setFeedback(feedback);
                setSubmitting(false);
            }
        }).catch((error: Promise<{ detail: string }>) => {
            error.then(error => {
                toast(error.detail, { position: 'top-center', type: 'error' });
            });
        });
    }, [feedback, notification, notification_id, context.user.username]);

    // Re-check for a feedback submitted elsewhere (e.g. another tab) whenever this tab
    // becomes visible again, but only while this notification has no feedback yet.
    // Once a feedback is set (found here or submitted), the cleanup removes the listener.
    useEffect(() => {
        if (feedback) return;

        const refocusAction = () => {
            if (document.visibilityState !== "visible") return;
            console.log("Tab achieved focus -- should rerun the fetch check for the feedback's existence");

            fetch(`/api/feedbacks/feedback-total/?partner=${partner_abbr}&notification_id=${notification_id}`, { credentials: "include" })
                .then(response => response.json())
                .then(json => {
                    setFeedback(json[0]);
                    if (json[0]) {
                        setObserved(json[0].flood_obs === "Yes");
                        setFloodObs(json[0].flood_obs);
                        toast("Detected a new feedback for this notification since the page was opened. The fields below have been updated.", { position: 'top-center', type: 'warning' });
                    }
                });
        };

        window.addEventListener("visibilitychange", refocusAction);
        return () => window.removeEventListener("visibilitychange", refocusAction);
    }, [feedback, notification_id, partner_abbr]);

    useEffect(() => {
        if (!notification) return;

        const m_closing_date = notification.closing_date ? moment(notification.closing_date) : moment();
        // set the date to next year on Jan 31st 23:59:59
        const m_end_date = m_closing_date.clone().add(1, 'year').month(0).date(31).hour(23).minute(59).second(59);
        console.log("m_closing_date", m_closing_date, "m_end_date", m_end_date, "locked", !m_end_date.isAfter(moment()));
        setEndDate(m_end_date.toLocaleString());
        setDaysLeft(m_end_date.fromNow());
        setFeedbackLocked(!m_end_date.isAfter(moment()));

    }, [notification]);

    useEffect(() => {
        setTitle("Feedback Detail");
        setExtraMenuLinks(NotificationNavLinks, "Feedbacks");

        window.scrollTo({ top: 0, behavior: 'instant' });
        fetch(`/api/notifications/sent/?notification_id=${notification_id}`)
            .then(response => response.json())
            .then(json => setNotification(json[0]));
    }, [notification_id, setExtraMenuLinks]);

    return (<>
        <div className='d-flex flex-row flex-lg-nowrap flex-wrap justify-content-between mb-3'>
            <SuperEditorContext>
                <EfasLink testid="admin-button" target="_blank" href={`/api/admin/efas_feedbacks/feedback/${feedback?.id}/change/`} className="btn btn-danger">Admin page</EfasLink>
            </SuperEditorContext>
            <EditorContext>
                <div data-testid="editor-button" role='button' className="btn btn-danger" onClick={() => setDisabled(!disabled)}>EFAS Editor - {disabled ? "Enable editing" : "Disable editing"}</div>
            </EditorContext>
        </div>
        <ToastContainer />

        <form data-testid="feedback-form" onSubmit={onSubmit}>
            <div className='d-flex flex-row flex-lg-nowrap flex-wrap justify-content-between'>
                <h1>Overview</h1>
                <SetReturnStateLink
                    testid='notification-detail-button' to={`/notifications-archive/detail/${notification_id}`}>Notification Detail <FontAwesomeIcon icon={faLink} /></SetReturnStateLink>
            </div>
            <div className='d-flex m-auto justify-content-center justify-content-xxl-between'>
                <div className='d-flex flex-column feedback-userinfo-row-heading' data-testid="user-info">
                    <h3>User info</h3>
                    <span><b>User:</b> {feedback?.user || context.user.email}</span>
                    <span><b>Partner:</b> {feedback?.partner_abbr || context.user.partner_abbr}</span>
                </div>
                <ObjectSuspense fallback={<LoadingSpinner />} track={notification}>
                    {notification?.notification_type === 3 ?
                        <FlashFeedbackInfo notification={notification} feedback={feedback} disabled={disabled} />
                        : <FloodFeedbackInfo notification={notification} feedback={feedback} />}
                </ObjectSuspense>
            </div>
            <hr />
            <ObjectSuspense fallback={<LoadingSpinner />} track={endDate && daysLeft && !submitting}>
                {canBeEnabled ?
                    <div data-testid="feedback-provided-info-window" className='d-flex flex-column py-2 border text-center'>
                        {feedback ?
                            <div className='p-3'>Feedback was already provided by <strong>{feedback?.user}</strong> from your organisation <SetReturnStateLink to={makePartnerSentRoute(context.user.partner_abbr)}><strong>{context.user.partner_abbr}</strong></SetReturnStateLink></div>
                            : null}
                        {!feedbackLocked ?
                            <>
                                <p>Feedback for this notification <strong>cannot be edited</strong> after: {endDate}, {daysLeft}</p>
                                <p>You can enable the fields below to edit it and update the information.</p>
                                <div className='mx-auto'>
                                    <div data-testid="enable-button" role='button' className={`btn btn-outline-dark ${disabled ? "color-11" : "color-6"}`} onClick={() => setDisabled(!disabled)}>{disabled ? "Enable" : "Disable"}</div>
                                </div>
                            </>
                            :
                            <>
                                <p>Feedback deadline has passed and cannot be edited anymore.</p><p> The deadline was {endDate}, {daysLeft}.</p>
                            </>}
                    </div>
                    : null}
            </ObjectSuspense>

            <div data-testid="flood_obs" className='d-flex flex-row flex-lg-nowrap flex-wrap py-3'>
                <label htmlFor='flood_obs' className='feedback-about-row-heading flex-fill'>Was a flood observed in or around the area? *</label>
                <ButtonGroup key={floodObs} name='flood_obs' className='feedback-about-row-body' disabled={disabled} required={true} checkedValue={floodObs} values={[
                    { name: "Yes", value: "Yes", color: "color-11" },
                    { name: "No", value: "No", color: "color-6" },
                ]} onChange={(value) => setObserved(value === "Yes")} />
            </div>

            <ObjectSuspense fallback={<LoadingSpinner />} track={notification}>
                {notification?.notification_type === 3 ?
                    <FlashFloodForm feedback={feedback} disabled={disabled} />
                    :
                    <>
                        {observed ? <ObservedForm feedback={feedback} disabled={disabled} selectedCausesRef={selectedCauses} /> : <NotObservedForm feedback={feedback} disabled={disabled} />}
                    </>
                }
                {!disabled ?
                    <div className='d-flex flex-row flex-lg-nowrap flex-wrap pb-5'>
                        <div className=' mx-auto'>
                            <button data-testid="submit-button" className="btn btn-outline-dark" type="submit">{feedback ? "Update" : "Submit"}</button>
                        </div>
                    </div>
                    : null}
            </ObjectSuspense>
        </form>
    </>);
});

